/**
 * A FRISSÍTÉS GOMB (#54) — a fejléc „Tensoft HH:MM"-je és a kérés követése.
 *
 * A VÁZBAN ÉL, NEM A MUNKALISTÁN. Az adat a bérlő egészére vonatkozik, nem egy
 * bizonylatra, és a fejléc mindkét felületen látszik (artboard, A változat —
 * Viktor, 10-05). A `t` és a palettaparancs is mindkét felületről elérhető.
 *
 * ⚠️ A KÉRÉS A SZERVEREN ÉL, nem itt. Egy kolléga kérése, egy újratöltött lap
 * vagy egy második ablak ugyanazt látja — a gomb ezért a szerver állapotából
 * tiltódik le, nem egy helyi „elküldtem" jelzőből.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { ApiError, api } from '@/lib/api-client';
import {
  EMPTY_FOLLOW,
  type FollowMemory,
  followStep,
  inFlight,
  type Outcome,
  type TensoftRefreshStatus,
} from './tensoftRefresh';

/**
 * MILYEN SŰRŰN KÉRDEZÜNK. Menet közben 3 másodperc: a gép percenként kérdez, a
 * pillanatkép 30–41 mp, tehát a teljes kör 1–3 perc — ennél ritkábban a
 * „készül" lépés kimaradna. Nyugalomban egy perc: a napi kör és egy kolléga
 * kérése ennyi késéssel is időben látszik.
 */
export const BUSY_POLL_MS = 3_000;
export const IDLE_POLL_MS = 60_000;

export interface TensoftRefreshHook {
  /** `null`, amíg nincs válasz — vagy ha a lekérdezés nem sikerült. */
  status: TensoftRefreshStatus | null;
  /** A gomb nem nyomható: a kérés úton van, vagy teljesítés alatt áll. */
  busy: boolean;
  /** A lezárult kérés kimenete — amíg a könyvelő nem nyugtázza. */
  outcome: Outcome | null;
  request: () => void;
  dismiss: () => void;
}

/**
 * @param onArrived Megérkezett a friss adat — a lista CSENDES frissítése
 *   (Viktor, 10-05: „magától"). Referencián utazik, mint a `useNavSync`-ben:
 *   a hívó minden renderben új függvényt adna, és a lekérdezés újraindulna.
 */
export function useTensoftRefresh(onArrived?: () => void): TensoftRefreshHook {
  const { signOut } = useAuth();
  const [status, setStatus] = useState<TensoftRefreshStatus | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [sending, setSending] = useState(false);

  const arrived = useRef(onArrived);
  useEffect(() => {
    arrived.current = onArrived;
  });

  const memory = useRef<FollowMemory>(EMPTY_FOLLOW);
  /** A lekérdezés soron kívüli indítása — a gombnyomás után ne egy percet várjunk. */
  const wake = useRef<() => void>(() => undefined);
  /** A zár referencián, nem állapoton — a `useNavSync` `inFlight`-jának érvelésével. */
  const posting = useRef(false);

  const apply = useCallback((next: TensoftRefreshStatus) => {
    const step = followStep(memory.current, next);
    memory.current = step.memory;
    setStatus(next);
    if (step.outcome !== null) setOutcome(step.outcome);
    if (step.reload) arrived.current?.();
  }, []);

  /**
   * A LEKÉRDEZÉS. `setTimeout`-lánc, nem `setInterval` (`useNavSync`): egy
   * lassú válasz mellett a kérések nem futnak egymásra.
   *
   * ⚠️ A HIBA NEM ÁLLÍTJA LE, és nem is látszik. A fejléc egy jelzés, nem egy
   * művelet: ha egy lekérdezés elmarad, a következő pótolja — egy hibasáv a
   * munka fölött itt többet ártana, mint a percnyi késés.
   */
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      clearTimeout(timer);
      const next = await fetchStatus();
      if (stopped) return;
      if (next === 'expired') {
        signOut('expired');
        return;
      }
      if (next !== null) apply(next);
      const busy = next !== null && inFlight(next.request);
      timer = setTimeout(() => void tick(), busy ? BUSY_POLL_MS : IDLE_POLL_MS);
    };

    wake.current = () => {
      clearTimeout(timer);
      timer = setTimeout(() => void tick(), BUSY_POLL_MS);
    };
    void tick();

    return () => {
      stopped = true;
      clearTimeout(timer);
      wake.current = () => undefined;
    };
  }, [apply, signOut]);

  const busy = sending || inFlight(status?.request ?? null);

  const request = useCallback(() => {
    if (posting.current || inFlight(status?.request ?? null)) return;
    posting.current = true;
    setSending(true);
    setOutcome(null);

    void api<TensoftRefreshStatus>('/tensoft/refresh-requests', { method: 'POST' })
      .then((next) => {
        apply(next);
        wake.current();
      })
      .catch((caught: unknown) => {
        const httpStatus = caught instanceof ApiError ? caught.status : 0;
        if (httpStatus === 401) {
          signOut('expired');
          return;
        }
        setOutcome({ kind: httpStatus === 403 ? 'forbidden' : 'sendFailed' });
      })
      .finally(() => {
        posting.current = false;
        setSending(false);
      });
  }, [apply, signOut, status]);

  const dismiss = useCallback(() => setOutcome(null), []);

  return { status, busy, outcome, request, dismiss };
}

/** Egy lekérdezés. `null`: elmaradt (a következő kör pótolja); `expired`: lejárt a munkamenet. */
async function fetchStatus(): Promise<TensoftRefreshStatus | 'expired' | null> {
  try {
    const body = await api<unknown>('/tensoft/refresh-status');
    // AZ ALAKOT IS NÉZZÜK: egy idegen válasz (proxy-hibaoldal, régi backend)
    // különben a követés közepén dobna, és a lekérdezés-lánc megszakadna.
    return isStatus(body) ? body : null;
  } catch (caught: unknown) {
    return caught instanceof ApiError && caught.status === 401 ? 'expired' : null;
  }
}

function isStatus(body: unknown): body is TensoftRefreshStatus {
  return (
    typeof body === 'object' &&
    body !== null &&
    'dataAsOf' in body &&
    'request' in body &&
    'agent' in body
  );
}
